/**
 * API Service - replacement for @amzn/wish-web-core-common ApiService
 */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpMethod, AuthenticationMethod, RequestOptions, ApiServiceConfig, HttpResponse } from './http-types';

export class ApiService {
    private axiosInstance: AxiosInstance;
    private config: ApiServiceConfig;

    constructor(config: ApiServiceConfig) {
        this.config = config;
        this.axiosInstance = axios.create({
            baseURL: config.baseUrl,
            timeout: config.timeout || 30000,
            headers: {
                'Content-Type': 'application/json',
            }
        });

        // Add request interceptor for authentication
        this.axiosInstance.interceptors.request.use(
            (config) => {
                // Add any default headers or authentication here
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Add response interceptor for error handling
        this.axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                console.error('API Request Error:', error);
                return Promise.reject(error);
            }
        );
    }

    public async request<T = any>(
        description: string,
        method: HttpMethod,
        url: string,
        data: any = {},
        options: RequestOptions = {}
    ): Promise<HttpResponse<T>> {
        try {
            const config: AxiosRequestConfig = {
                method: method.toLowerCase() as any,
                url,
                headers: {
                    ...options.httpConfig?.headers
                }
            };

            // Add data based on method
            if (method === HttpMethod.GET || method === HttpMethod.DELETE) {
                config.params = data;
            } else {
                config.data = data;
            }

            // Add timeout if specified
            if (options.httpConfig?.timeout) {
                config.timeout = options.httpConfig.timeout;
            }

            const response: AxiosResponse<T> = await this.axiosInstance.request(config);

            return {
                data: response.data,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers
            };
        } catch (error: any) {
            console.error(`API Error [${description}]:`, error);
            throw error;
        }
    }

    // Convenience methods
    public get<T = any>(url: string, params?: any, options?: RequestOptions): Promise<HttpResponse<T>> {
        return this.request<T>("GET request", HttpMethod.GET, url, params, options);
    }

    public post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<HttpResponse<T>> {
        return this.request<T>("POST request", HttpMethod.POST, url, data, options);
    }

    public put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<HttpResponse<T>> {
        return this.request<T>("PUT request", HttpMethod.PUT, url, data, options);
    }

    public delete<T = any>(url: string, params?: any, options?: RequestOptions): Promise<HttpResponse<T>> {
        return this.request<T>("DELETE request", HttpMethod.DELETE, url, params, options);
    }

    public patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<HttpResponse<T>> {
        return this.request<T>("PATCH request", HttpMethod.PATCH, url, data, options);
    }
}
